import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { connectParityApi, normalizeUserId } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { EVERYONE_ID, MENTION_ALL_ATTR, MENTION_ATTR } from "@/lib/mentions";
import { composeSendHtml, richIsEmpty, richToPlain, serializeRich, serializeRichHtml } from "@/lib/richHtml";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { useSettings } from "@/lib/settings";
import { cn } from "@/lib/utils";
import { ChatAvatar } from "../chat/chatUi";
import {
    applyFormat, applyLink, clearFormatting, closestIn, formatAt, insertRule, linkContext, pruneEmptyInline, removeLink,
    restoreSelection, selectionRange, setSelection, snapshotSelection, toggleCodeBlock, toggleInlineCode, toggleQuote, topBlocks,
    type LinkContext, type SelectionSnapshot
} from "./richDom";

/** Someone who can be @mentioned: the chat's members are offered first. */
export interface MentionCandidate {
    userId: string;
    name: string;
    email: string;
}

interface MentionOption extends MentionCandidate {
    /** Not in this chat: the name is highlighted but nobody is notified. */
    outside: boolean;
}

/** The formats at the caret, for the toolbar's pressed states. */
export interface ActiveFormats {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    ul: boolean;
    ol: boolean;
    quote: boolean;
    code: boolean;
    codeBlock: boolean;
    link: boolean;
    inList: boolean;
    color: string | null;
    highlight: string | null;
    size: string | null;
    /** The font family's id (`fg-f-<id>`); null = the default font. */
    font: string | null;
}

export const NO_FORMATS: ActiveFormats = {
    bold: false, italic: false, underline: false, strike: false, ul: false, ol: false, quote: false, code: false,
    codeBlock: false, link: false, inList: false, color: null, highlight: null, size: null, font: null
};

export type SimpleCommandType = "bold" | "italic" | "underline" | "strike" | "ul" | "ol" | "quote" | "code" | "code-inline" | "code-block" | "hr" | "clear" | "indent" | "outdent" | "undo" | "redo" | "unlink";

export type RichCommand =
    | { type: SimpleCommandType }
    | { type: "color" | "highlight" | "size" | "font"; id: string | null };

export interface EditorState {
    html: string;
    text: string;
    empty: boolean;
}

export interface RichEditorHandle {
    focus: () => void;
    /** The content to send: serialised, with typed Markdown applied. */
    getHtml: () => string;
    /** The content as the editor holds it (for drafts): serialised, no Markdown applied. */
    getDraftHtml: () => string;
    getText: () => string;
    isEmpty: () => boolean;
    /** Replace the content. `silent` skips onChange (restoring a saved draft is not typing). */
    setHtml: (html: string, silent?: boolean) => void;
    clear: () => void;
    insertText: (text: string) => void;
    /** Put a quote of someone's message above what is typed. */
    insertQuote: (author: string, text: string) => void;
    run: (command: RichCommand) => void;
    activeFormats: () => ActiveFormats;
    /** Remember the selection and describe the link under it, for the Link dialog. */
    beginLink: () => LinkContext;
    commitLink: (text: string, href: string) => void;
    element: () => HTMLElement | null;
}

export interface RichEditorProps {
    initialHtml?: string;
    placeholder: string;
    ariaLabel?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    className?: string;
    /** Enter saves (edit boxes) instead of following Settings > General. */
    enterAlwaysSubmits?: boolean;
    /** Keep pictures of an existing message when it is edited. */
    keepImages?: boolean;
    mentionCandidates?: MentionCandidate[];
    /** Offer "@everyone". */
    everyone?: boolean;
    onChange?: (state: EditorState) => void;
    onSubmit?: () => void;
    onEscape?: () => void;
    onEditLast?: () => void;
    /** Return true when the paste carried files and was handled. */
    onPaste?: (e: React.ClipboardEvent) => boolean;
    onSelectionChange?: () => void;
    /** Ctrl+K */
    onRequestLink?: () => void;
}

const ZWSP = /\u200B/g;
const BLOCK_ROOT = new Set(["P", "DIV", "UL", "OL", "BLOCKQUOTE", "PRE", "HR", "H1", "H2", "H3", "H4", "H5", "H6", "TABLE"]);

function isDomEmpty(root: HTMLElement): boolean {
    if (root.querySelector("hr,img," + `[${MENTION_ATTR}],[${MENTION_ALL_ATTR}]`)) return false;
    return (root.textContent ?? "").replace(ZWSP, "").trim().length === 0;
}

/** Editor-ready DOM from stored HTML: sanitised, chips made non-editable, always at least one block. */
export function prepareEditorHtml(html: string): string {
    const clean = sanitizeHtml(html);
    if (!clean.trim()) return "<p><br></p>";
    const doc = new DOMParser().parseFromString(clean, "text/html");
    doc.body.querySelectorAll(`[${MENTION_ATTR}],[${MENTION_ALL_ATTR}]`).forEach((el) => el.setAttribute("contenteditable", "false"));
    // Whitespace between blocks would become stray text nodes.
    Array.from(doc.body.childNodes).forEach((n) => { if (n.nodeType === 3 && !(n.nodeValue ?? "").trim()) n.remove(); });
    return doc.body.innerHTML || "<p><br></p>";
}

function ensureBlocks(root: HTMLElement): void {
    if (!root.firstChild) {
        root.innerHTML = "<p><br></p>";
        const r = document.createRange();
        r.setStart(root.firstChild!, 0);
        r.collapse(true);
        setSelection(r);
        return;
    }
    const sel = window.getSelection();
    const keep = sel && sel.rangeCount > 0 && root.contains(sel.anchorNode)
        ? { an: sel.anchorNode!, ao: sel.anchorOffset, fn: sel.focusNode!, fo: sel.focusOffset }
        : null;
    let run: Node[] = [];
    let changed = false;
    const flush = () => {
        if (run.length === 0) return;
        if (run.every((n) => n.nodeType === 3 && !(n as Text).data.trim())) run.forEach((n) => n.parentNode?.removeChild(n));
        else {
            const p = document.createElement("p");
            run[0]!.parentNode!.insertBefore(p, run[0]!);
            run.forEach((n) => p.appendChild(n));
            changed = true;
        }
        run = [];
    };
    for (const child of Array.from(root.childNodes)) {
        if (child.nodeType === 1 && BLOCK_ROOT.has((child as Element).tagName)) flush();
        else run.push(child);
    }
    flush();
    if (changed && keep && keep.an.isConnected && keep.fn.isConnected) {
        sel!.setBaseAndExtent(keep.an, keep.ao, keep.fn, keep.fo);
    }
}

function caretBlock(root: HTMLElement): HTMLElement | null {
    const sel = window.getSelection();
    let n: Node | null = sel?.focusNode ?? null;
    if (!n || !root.contains(n)) return null;
    while (n && n.parentNode !== root) n = n.parentNode;
    return n && n.nodeType === 1 ? (n as HTMLElement) : null;
}

/**
 * The text area of the rich composer: a small contenteditable the app owns. It keeps its own undo
 * history (so colour, link, code and quote edits undo like typing), turns "@" into a people picker
 * that inserts a non-editable chip, and hands back HTML in the composer's contract (lib/richHtml.ts).
 */
export const RichEditor = forwardRef<RichEditorHandle, RichEditorProps>(function RichEditor({
    initialHtml, placeholder, ariaLabel, disabled, autoFocus, className, enterAlwaysSubmits, keepImages,
    mentionCandidates, everyone, onChange, onSubmit, onEscape, onEditLast, onPaste, onSelectionChange, onRequestLink
}, ref) {
    const { enterToSend } = useSettings();
    const token = useAccessToken();
    const rootRef = useRef<HTMLDivElement>(null);
    const [empty, setEmpty] = useState(() => richIsEmpty(initialHtml ?? ""));
    const lastRange = useRef<Range | null>(null);
    const linkRange = useRef<Range | null>(null);
    const composing = useRef(false);

    // Latest props for handlers registered once.
    const propsRef = useRef({ onChange, onSubmit, onEscape, onEditLast, onSelectionChange, onRequestLink, keepImages });
    propsRef.current = { onChange, onSubmit, onEscape, onEditLast, onSelectionChange, onRequestLink, keepImages };

    // ── undo history ──────────────────────────────────────────────────────
    const history = useRef<{ stack: { html: string; sel: SelectionSnapshot | null }[]; index: number }>({ stack: [], index: -1 });
    const snapTimer = useRef<number | undefined>(undefined);
    const commit = useCallback((force = false) => {
        const root = rootRef.current;
        if (!root) return;
        if (snapTimer.current !== undefined) { window.clearTimeout(snapTimer.current); snapTimer.current = undefined; }
        const h = history.current;
        const html = root.innerHTML;
        const top = h.stack[h.index];
        if (top && top.html === html && !force) { top.sel = snapshotSelection(root) ?? top.sel; return; }
        h.stack = h.stack.slice(0, h.index + 1);
        h.stack.push({ html, sel: snapshotSelection(root) });
        if (h.stack.length > 200) h.stack.shift();
        h.index = h.stack.length - 1;
    }, []);
    const scheduleCommit = useCallback(() => {
        if (snapTimer.current !== undefined) window.clearTimeout(snapTimer.current);
        snapTimer.current = window.setTimeout(() => commit(), 450);
    }, [commit]);
    const emit = useCallback(() => {
        const root = rootRef.current;
        if (!root) return;
        const isEmpty = isDomEmpty(root);
        setEmpty(isEmpty);
        propsRef.current.onChange?.({ html: isEmpty ? "" : serializeRich(root, { keepImages: propsRef.current.keepImages }), text: richToPlain(root), empty: isEmpty });
    }, []);
    const travel = useCallback((dir: -1 | 1) => {
        const root = rootRef.current;
        if (!root) return;
        commit();
        const h = history.current;
        const next = h.index + dir;
        if (next < 0 || next >= h.stack.length) return;
        h.index = next;
        const entry = h.stack[next]!;
        root.innerHTML = entry.html;
        restoreSelection(root, entry.sel);
        emit();
    }, [commit, emit]);

    /** Run a DOM edit as one undo step: state before, edit, normalise, state after. */
    const mutate = useCallback((edit: (root: HTMLElement) => void) => {
        const root = rootRef.current;
        if (!root) return;
        if (document.activeElement !== root) {
            root.focus({ preventScroll: true });
            if (lastRange.current) setSelection(lastRange.current);
        }
        commit();
        edit(root);
        pruneEmptyInline(root);
        ensureBlocks(root);
        commit();
        emit();
    }, [commit, emit]);

    // ── initial content ───────────────────────────────────────────────────
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        root.innerHTML = prepareEditorHtml(initialHtml ?? "");
        history.current = { stack: [], index: -1 };
        commit();
        setEmpty(isDomEmpty(root));
        try { document.execCommand("defaultParagraphSeparator", false, "p"); document.execCommand("styleWithCSS", false, "false"); } catch { /* not supported */ }
        if (autoFocus) {
            root.focus();
            const r = document.createRange();
            r.selectNodeContents(root);
            r.collapse(false);
            setSelection(r);
        }
        // Only the first render's content seeds the editor; later changes go through the handle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => { if (snapTimer.current !== undefined) window.clearTimeout(snapTimer.current); }, []);

    // ── @mentions ─────────────────────────────────────────────────────────
    const listId = useId();
    const [mention, setMention] = useState<{ node: Text; start: number; query: string } | null>(null);
    const [highlight, setHighlight] = useState(0);
    const [dirQuery, setDirQuery] = useState("");
    const mentionsOn = Boolean(mentionCandidates);

    useEffect(() => {
        const q = mention?.query.trim() ?? "";
        const t = window.setTimeout(() => setDirQuery(q.length >= 2 ? q : ""), 200);
        return () => window.clearTimeout(t);
    }, [mention?.query]);
    const directory = useQuery({
        queryKey: ["connect-dir-search", dirQuery, token],
        queryFn: () => connectParityApi.searchDirectory(dirQuery, token),
        enabled: Boolean(mentionsOn && token && dirQuery.length >= 2),
        staleTime: 30_000
    });

    const options = useMemo<MentionOption[]>(() => {
        if (!mention || !mentionCandidates) return [];
        const q = mention.query.toLowerCase();
        const all: MentionOption[] = everyone && mentionCandidates.length > 1 && ("everyone".startsWith(q) || "channel".startsWith(q) || "all".startsWith(q))
            ? [{ userId: EVERYONE_ID, name: "everyone", email: "Notify all members of this chat", outside: false }]
            : [];
        const members = mentionCandidates
            .filter((c) => !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
            .slice(0, 6)
            .map((c) => ({ ...c, outside: false }));
        const memberIds = new Set(mentionCandidates.map((c) => normalizeUserId(c.userId)));
        const others = q.length >= 2
            ? (directory.data ?? [])
                .filter((u) => !memberIds.has(normalizeUserId(u.userId)))
                .slice(0, 5)
                .map((u) => ({ userId: u.userId, name: u.displayName || u.email, email: u.email, outside: true }))
            : [];
        return [...all, ...members, ...others];
    }, [mention, mentionCandidates, directory.data, everyone]);
    const menuOpen = mention !== null && options.length > 0;
    useEffect(() => { setHighlight(0); }, [mention?.start, options.length]);

    /** Look for "@token" right before the caret (not inside a word, an address, a code block or a chip). */
    const scanMention = useCallback(() => {
        const root = rootRef.current;
        if (!root || !mentionsOn) { setMention(null); return; }
        const sel = window.getSelection();
        if (!sel || !sel.isCollapsed || !sel.focusNode || sel.focusNode.nodeType !== 3 || !root.contains(sel.focusNode)) { setMention(null); return; }
        const node = sel.focusNode as Text;
        if (closestIn(node, root, (el) => el.tagName === "PRE" || el.tagName === "CODE" || el.hasAttribute(MENTION_ATTR))) { setMention(null); return; }
        const before = node.data.slice(Math.max(0, sel.focusOffset - 50), sel.focusOffset);
        const m = /(^|[\s(\u00a0])@([^\s@\u00a0]{0,40})$/.exec(before);
        if (!m) { setMention(null); return; }
        setMention({ node, start: sel.focusOffset - m[2]!.length - 1, query: m[2]! });
    }, [mentionsOn]);

    const pick = (option: MentionOption) => {
        const root = rootRef.current;
        if (!mention || !root || !mention.node.isConnected) return;
        const sel = window.getSelection();
        const end = sel && sel.focusNode === mention.node ? sel.focusOffset : mention.node.length;
        commit();
        const r = document.createRange();
        r.setStart(mention.node, mention.start);
        r.setEnd(mention.node, Math.max(mention.start, end));
        r.deleteContents();
        const chip = document.createElement("span");
        chip.className = "fg-mention";
        chip.setAttribute("contenteditable", "false");
        if (option.userId === EVERYONE_ID) chip.setAttribute(MENTION_ALL_ATTR, "1");
        else chip.setAttribute(MENTION_ATTR, normalizeUserId(option.userId));
        chip.textContent = `@${option.name}`;
        r.insertNode(chip);
        const space = document.createTextNode("\u00a0");
        chip.after(space);
        const caret = document.createRange();
        caret.setStart(space, 1);
        caret.collapse(true);
        root.focus({ preventScroll: true });
        setSelection(caret);
        setMention(null);
        commit();
        emit();
    };

    // ── selection tracking (toolbar state, last caret, mention scan) ─────
    useEffect(() => {
        const onSel = () => {
            const root = rootRef.current;
            if (!root) return;
            const r = selectionRange(root);
            if (r) lastRange.current = r.cloneRange();
            if (document.activeElement === root) scanMention();
            if (r) propsRef.current.onSelectionChange?.();
        };
        document.addEventListener("selectionchange", onSel);
        return () => document.removeEventListener("selectionchange", onSel);
    }, [scanMention]);

    // ── commands ─────────────────────────────────────────────────────────
    const exec = (name: string) => { try { document.execCommand(name); } catch { /* ignore */ } };

    const run = useCallback((cmd: RichCommand) => {
        const root = rootRef.current;
        if (!root) return;
        if (cmd.type === "undo") { travel(-1); return; }
        if (cmd.type === "redo") { travel(1); return; }
        mutate((r) => {
            switch (cmd.type) {
                case "bold": exec("bold"); break;
                case "italic": exec("italic"); break;
                case "underline": exec("underline"); break;
                case "strike": exec("strikeThrough"); break;
                case "ul": exec("insertUnorderedList"); break;
                case "ol": exec("insertOrderedList"); break;
                case "indent": if (closestIn(window.getSelection()?.focusNode ?? null, r, (e) => e.tagName === "LI")) exec("indent"); break;
                case "outdent": if (closestIn(window.getSelection()?.focusNode ?? null, r, (e) => e.tagName === "LI")) exec("outdent"); break;
                case "quote": toggleQuote(r); break;
                case "code-inline": toggleInlineCode(r); break;
                case "code-block": toggleCodeBlock(r); break;
                case "code": {
                    const range = selectionRange(r);
                    const multi = range && !range.collapsed && topBlocks(r, range).length > 1;
                    if (multi || closestIn(window.getSelection()?.focusNode ?? null, r, (e) => e.tagName === "PRE")) toggleCodeBlock(r);
                    else toggleInlineCode(r);
                    break;
                }
                case "hr": insertRule(r); break;
                case "clear": clearFormatting(r); break;
                case "unlink": removeLink(r); break;
                case "color": applyFormat(r, "c", cmd.id); break;
                case "highlight": applyFormat(r, "h", cmd.id); break;
                case "size": applyFormat(r, "s", cmd.id); break;
                case "font": applyFormat(r, "f", cmd.id); break;
            }
        });
    }, [mutate, travel]);

    const activeFormats = useCallback((): ActiveFormats => {
        const root = rootRef.current;
        if (!root) return NO_FORMATS;
        const sel = window.getSelection();
        const node = sel?.focusNode ?? null;
        if (!node || !root.contains(node)) return NO_FORMATS;
        const state = (c: string) => { try { return document.queryCommandState(c); } catch { return false; } };
        const inTag = (...tags: string[]) => !!closestIn(node, root, (el) => tags.includes(el.tagName));
        const list = closestIn(node, root, (el) => el.tagName === "UL" || el.tagName === "OL");
        return {
            bold: state("bold"),
            italic: state("italic"),
            underline: state("underline"),
            strike: state("strikeThrough"),
            ul: list?.tagName === "UL",
            ol: list?.tagName === "OL",
            quote: inTag("BLOCKQUOTE"),
            code: inTag("CODE"),
            codeBlock: inTag("PRE"),
            link: inTag("A"),
            inList: inTag("LI"),
            color: formatAt(root, "c"),
            highlight: formatAt(root, "h"),
            size: formatAt(root, "s"),
            font: formatAt(root, "f")
        };
    }, []);

    // ── imperative handle ────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
        focus: () => {
            const root = rootRef.current;
            if (!root) return;
            root.focus({ preventScroll: true });
            if (lastRange.current && root.contains(lastRange.current.startContainer)) setSelection(lastRange.current);
        },
        getHtml: () => {
            const root = rootRef.current;
            return !root || isDomEmpty(root) ? "" : composeSendHtml(root, { keepImages: propsRef.current.keepImages });
        },
        getDraftHtml: () => {
            const root = rootRef.current;
            return !root || isDomEmpty(root) ? "" : serializeRich(root, { keepImages: true });
        },
        getText: () => (rootRef.current ? richToPlain(rootRef.current) : ""),
        isEmpty: () => (rootRef.current ? isDomEmpty(rootRef.current) : true),
        setHtml: (html: string, silent?: boolean) => {
            const root = rootRef.current;
            if (!root) return;
            root.innerHTML = prepareEditorHtml(html);
            commit(true);
            if (silent) setEmpty(isDomEmpty(root)); else emit();
        },
        clear: () => {
            const root = rootRef.current;
            if (!root) return;
            root.innerHTML = "<p><br></p>";
            const r = document.createRange();
            r.setStart(root.firstChild!, 0);
            r.collapse(true);
            if (document.activeElement === root) setSelection(r);
            lastRange.current = null;
            commit(true);
            emit();
        },
        insertText: (text: string) => {
            const root = rootRef.current;
            if (!root) return;
            root.focus({ preventScroll: true });
            if (lastRange.current && root.contains(lastRange.current.startContainer)) setSelection(lastRange.current);
            commit();
            try { document.execCommand("insertText", false, text); } catch { /* ignore */ }
            commit();
            emit();
        },
        insertQuote: (author: string, text: string) => {
            const root = rootRef.current;
            if (!root) return;
            commit();
            const q = document.createElement("blockquote");
            for (const line of `${author}: ${text}`.split(/\n> ?|\n/)) {
                const p = document.createElement("p");
                if (line) p.textContent = line; else p.appendChild(document.createElement("br"));
                q.appendChild(p);
            }
            const wasEmpty = isDomEmpty(root);
            if (wasEmpty) root.textContent = "";
            root.insertBefore(q, root.firstChild);
            if (wasEmpty) {
                const tail = document.createElement("p");
                tail.appendChild(document.createElement("br"));
                root.appendChild(tail);
            }
            root.focus({ preventScroll: true });
            const r = document.createRange();
            r.selectNodeContents(root.lastElementChild ?? root);
            r.collapse(false);
            setSelection(r);
            commit(true);
            emit();
        },
        run,
        activeFormats,
        beginLink: () => {
            const root = rootRef.current;
            if (!root) return { text: "", href: "", existing: false };
            const r = selectionRange(root) ?? lastRange.current;
            linkRange.current = r ? r.cloneRange() : null;
            if (r && !selectionRange(root)) setSelection(r);
            return linkContext(root);
        },
        commitLink: (text: string, href: string) => {
            const root = rootRef.current;
            const range = linkRange.current;
            if (!root || !range || !root.contains(range.startContainer)) return;
            mutate((r) => {
                setSelection(range);
                applyLink(r, range, text, href);
            });
        },
        element: () => rootRef.current
    }), [commit, emit, mutate, run, activeFormats]);

    // ── keyboard, paste, composition ─────────────────────────────────────
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const root = rootRef.current;
        if (!root || composing.current || e.nativeEvent.isComposing) return;
        if (menuOpen) {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const n = options.length;
                setHighlight((h) => (e.key === "ArrowDown" ? (h + 1) % n : (h - 1 + n) % n));
                return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                const o = options[highlight] ?? options[0];
                if (o) pick(o);
                return;
            }
            if (e.key === "Escape") { e.preventDefault(); setMention(null); return; }
        }
        const mod = e.ctrlKey || e.metaKey;
        if (mod && !e.altKey) {
            const k = e.key.toLowerCase();
            const handled = (cmd: RichCommand) => { e.preventDefault(); run(cmd); };
            if (!e.shiftKey) {
                if (k === "b") return handled({ type: "bold" });
                if (k === "i") return handled({ type: "italic" });
                if (k === "u") return handled({ type: "underline" });
                if (k === "z") return handled({ type: "undo" });
                if (k === "y") return handled({ type: "redo" });
                if (k === "k") { e.preventDefault(); propsRef.current.onRequestLink?.(); return; }
            } else {
                if (e.code === "Digit5" || k === "%") return handled({ type: "strike" });
                if (k === "c") return handled({ type: "code" });
                if (k === "z") return handled({ type: "redo" });
                if (e.code === "Digit7" || k === "&") return handled({ type: "ol" });
                if (e.code === "Digit8" || k === "*") return handled({ type: "ul" });
                if (e.code === "Digit9" || k === "(") return handled({ type: "quote" });
            }
        }
        if (e.key === "Tab" && !mod && !e.altKey && closestIn(window.getSelection()?.focusNode ?? null, root, (el) => el.tagName === "LI")) {
            e.preventDefault();
            run({ type: e.shiftKey ? "outdent" : "indent" });
            return;
        }
        if (e.key === "Escape" && propsRef.current.onEscape) { e.preventDefault(); propsRef.current.onEscape(); return; }
        if (e.key === "Enter") {
            const block = caretBlock(root);
            const focus = window.getSelection()?.focusNode ?? null;
            const inPre = !!closestIn(focus, root, (el) => el.tagName === "PRE");
            const structured = inPre || !!closestIn(focus, root, (el) => el.tagName === "LI" || el.tagName === "BLOCKQUOTE");
            const sendCombo = enterAlwaysSubmits ? !e.shiftKey : enterToSend ? !e.shiftKey : e.ctrlKey || e.metaKey;
            // In a list, quote or code block, plain Enter keeps writing; Ctrl+Enter sends.
            if (propsRef.current.onSubmit && sendCombo && (!structured || e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                propsRef.current.onSubmit();
                return;
            }
            if (inPre && block) {
                e.preventDefault();
                preEnter(root, block, mutate);
                return;
            }
            if (block?.tagName === "BLOCKQUOTE" && !e.shiftKey) {
                const sel = window.getSelection();
                const p = sel?.focusNode ? closestIn(sel.focusNode, block, (el) => el.tagName === "P" || el.tagName === "DIV") : null;
                if (p && !(p.textContent ?? "").replace(ZWSP, "").trim()) {
                    e.preventDefault();
                    mutate(() => {
                        const para = document.createElement("p");
                        para.appendChild(document.createElement("br"));
                        p.remove();
                        block.after(para);
                        if (!block.firstElementChild) block.remove();
                        const r = document.createRange();
                        r.setStart(para, 0);
                        r.collapse(true);
                        setSelection(r);
                    });
                    return;
                }
            }
        }
        // As in Teams: Up arrow in an empty box edits your last message.
        if (e.key === "ArrowUp" && propsRef.current.onEditLast && isDomEmpty(root)) {
            e.preventDefault();
            propsRef.current.onEditLast();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (onPaste?.(e)) return;
        const root = rootRef.current;
        if (!root) return;
        e.preventDefault();
        const html = e.clipboardData.getData("text/html");
        const text = e.clipboardData.getData("text/plain");
        const inPre = !!closestIn(window.getSelection()?.focusNode ?? null, root, (el) => el.tagName === "PRE");
        commit();
        if (html && !inPre) {
            // Only the composer's own formats survive a paste: no fonts, sizes, colours or classes from elsewhere.
            const clean = serializeRichHtml(html.replace(/<!--[\s\S]*?-->/g, ""));
            if (clean) { try { document.execCommand("insertHTML", false, clean); } catch { /* ignore */ } }
            else if (text) { try { document.execCommand("insertText", false, text); } catch { /* ignore */ } }
        } else if (text) {
            try { document.execCommand("insertText", false, text); } catch { /* ignore */ }
        }
        ensureBlocks(root);
        commit();
        emit();
    };

    const onInput = () => {
        const root = rootRef.current;
        if (!root) return;
        if (!composing.current) ensureBlocks(root);
        emit();
        scheduleCommit();
    };

    const activeOption = menuOpen ? `${listId}-opt-${highlight}` : undefined;

    return (
        <div className="relative min-w-0 flex-1">
            {menuOpen ? (
                <ul
                    id={listId}
                    role="listbox"
                    aria-label="People to mention"
                    className="absolute bottom-full left-0 z-30 mb-1 max-h-72 w-80 max-w-[calc(100%-0.5rem)] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
                >
                    {options.map((o, i) => {
                        const firstOutside = o.outside && (i === 0 || !options[i - 1]!.outside);
                        return (
                            <li key={`${o.outside ? "dir" : "m"}-${o.userId}`} role="presentation">
                                {firstOutside ? (
                                    <div className="px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" role="presentation">
                                        Others in your organisation
                                    </div>
                                ) : null}
                                <div
                                    id={`${listId}-opt-${i}`}
                                    role="option"
                                    aria-selected={i === highlight}
                                    // mousedown keeps focus (and the caret) in the editor
                                    onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                                    onMouseEnter={() => setHighlight(i)}
                                    className={cn("flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm", i === highlight ? "bg-accent" : "hover:bg-accent/60")}
                                >
                                    <ChatAvatar name={o.name} size="sm" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate">{o.name}</span>
                                        <span className="block truncate text-[11px] text-muted-foreground">
                                            {o.outside ? "Not in this chat — won't be notified" : o.email}
                                        </span>
                                    </span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
            <div
                ref={rootRef}
                role="textbox"
                aria-multiline="true"
                aria-label={ariaLabel ?? placeholder}
                aria-autocomplete={mentionsOn ? "list" : undefined}
                aria-controls={menuOpen ? listId : undefined}
                aria-activedescendant={activeOption}
                aria-disabled={disabled || undefined}
                contentEditable={!disabled}
                suppressContentEditableWarning
                spellCheck
                data-placeholder={placeholder}
                data-empty={empty ? "true" : "false"}
                onKeyDown={onKeyDown}
                onInput={onInput}
                onPaste={handlePaste}
                onBeforeInput={(e) => {
                    const t = (e.nativeEvent as InputEvent).inputType;
                    // The editor keeps its own history so every kind of edit undoes the same way.
                    if (t === "historyUndo") { e.preventDefault(); travel(-1); }
                    else if (t === "historyRedo") { e.preventDefault(); travel(1); }
                }}
                onCompositionStart={() => { composing.current = true; }}
                onCompositionEnd={() => { composing.current = false; onInput(); }}
                onBlur={() => setMention(null)}
                className={cn("fg-editor fg-msg max-h-[200px] min-h-[36px] overflow-y-auto break-words px-1 py-2 text-sm outline-none", className)}
            />
        </div>
    );
});

/** Enter inside a code block: a new line; a second Enter on a blank last line leaves the block. */
function preEnter(root: HTMLElement, pre: HTMLElement, mutate: (edit: (root: HTMLElement) => void) => void): void {
    mutate(() => {
        const sel = window.getSelection();
        const range = selectionRange(root);
        if (!sel || !range) return;
        range.deleteContents();
        const probe = document.createRange();
        probe.selectNodeContents(pre);
        probe.setEnd(range.startContainer, range.startOffset);
        const before = probe.toString().replace(ZWSP, "");
        const full = (pre.textContent ?? "").replace(ZWSP, "");
        const after = full.slice(before.length);
        if (/\n$/.test(before) && /^\n*$/.test(after)) {
            const kept = before.replace(/\n+$/, "");
            const p = document.createElement("p");
            p.appendChild(document.createElement("br"));
            if (kept) pre.textContent = kept; else pre.remove();
            (kept ? pre : null)?.after(p);
            if (!kept) root.appendChild(p);
            const r = document.createRange();
            r.setStart(p, 0);
            r.collapse(true);
            setSelection(r);
            return;
        }
        // A trailing newline is not drawn, so the last line gets a spare one to show the new line.
        pre.textContent = before + "\n" + after + (after === "" ? "\n" : "");
        const r = document.createRange();
        r.setStart(pre.firstChild!, before.length + 1);
        r.collapse(true);
        setSelection(r);
    });
}

